export type ProviderId =
  | "scribe-groq"
  | "assemblyai-fast"
  | "assemblyai-accurate"
  | "assemblyai-wide"
  | "soniox"
  | "openai-live-groq"
  | "openai"
  | "gemini"
  | "gradium";

export interface ProviderStatus {
  ready: boolean;
  label: string;
  input: string;
  translation: string;
  preview: string;
  supportedSources?: string[];
  supportedTargets?: string[];
}

export interface StackStatus {
  ready: boolean;
  providers: Record<ProviderId, ProviderStatus>;
}

export interface GroqUsage {
  queue_time?: number;
  prompt_time?: number;
  completion_time?: number;
  total_time?: number;
  prompt_tokens?: number;
  completion_tokens?: number;
}

interface TranslationOptions {
  text: string;
  sourceLanguage: string;
  targetLanguage: string;
  mode: "provisional" | "committed";
  signal?: AbortSignal;
  onToken: (token: string, receivedAt: number) => void;
}

const readError = async (response: Response): Promise<string> => {
  try {
    const payload = await response.json() as { error?: string };
    return payload.error ?? `Request failed with status ${response.status}.`;
  } catch {
    return `Request failed with status ${response.status}.`;
  }
};

export async function getStackStatus(): Promise<StackStatus> {
  const response = await fetch("/api/status", { cache: "no-store" });
  if (!response.ok) throw new Error(await readError(response));
  return response.json() as Promise<StackStatus>;
}

export async function getElevenLabsToken(
  type: "realtime_scribe",
): Promise<string> {
  const response = await fetch("/api/eleven-token", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ type }),
  });
  if (!response.ok) throw new Error(await readError(response));
  const payload = await response.json() as { token: string };
  return payload.token;
}

const postSession = async <T>(
  path: string,
  body: { targetLanguage?: string; sourceLanguage?: string },
): Promise<T> => {
  const response = await fetch(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(await readError(response));
  return response.json() as Promise<T>;
};

export const getSonioxToken = (targetLanguage: string): Promise<{ token: string }> =>
  postSession("/api/soniox-token", { targetLanguage });

export const getOpenAIClientSecret = (targetLanguage: string): Promise<{ value: string }> =>
  postSession("/api/openai-session", { targetLanguage });

export async function openOpenAITranscriptionCall(
  sdp: string,
  sourceLanguage: string,
): Promise<string> {
  const response = await fetch(
    `/api/openai-transcribe-call?sourceLanguage=${encodeURIComponent(sourceLanguage)}`,
    {
      method: "POST",
      headers: { "content-type": "application/sdp" },
      body: sdp,
    },
  );
  if (!response.ok) throw new Error(await readError(response));
  return response.text();
}

export const getGeminiToken = (targetLanguage: string): Promise<{ token: string }> =>
  postSession("/api/gemini-token", { targetLanguage });

export const getGradiumToken = (targetLanguage: string): Promise<{ token: string }> =>
  postSession("/api/gradium-token", { targetLanguage });

export async function getAssemblyAIToken(): Promise<{ token: string }> {
  const response = await fetch("/api/assemblyai-token", {
    method: "POST",
    headers: { "content-type": "application/json" },
  });
  if (!response.ok) throw new Error(await readError(response));
  return response.json() as Promise<{ token: string }>;
}

export async function streamTranslation({
  text,
  sourceLanguage,
  targetLanguage,
  mode,
  signal,
  onToken,
}: TranslationOptions): Promise<GroqUsage | undefined> {
  const response = await fetch("/api/translate", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ text, sourceLanguage, targetLanguage, mode }),
    signal,
  });

  if (!response.ok || !response.body) {
    throw new Error(await readError(response));
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let usage: GroqUsage | undefined;

  const processLine = (line: string) => {
    if (!line.startsWith("data:")) return;
    const data = line.slice(5).trim();
    if (!data || data === "[DONE]") return;

    const chunk = JSON.parse(data) as {
      choices?: Array<{ delta?: { content?: string } }>;
      usage?: GroqUsage;
    };
    const token = chunk.choices?.[0]?.delta?.content;
    if (token) onToken(token, performance.now());
    if (chunk.usage) usage = chunk.usage;
  };

  while (true) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value, { stream: !done });
    const lines = buffer.split(/\r?\n/);
    buffer = lines.pop() ?? "";
    for (const line of lines) processLine(line);
    if (done) break;
  }

  if (buffer.trim()) processLine(buffer);
  return usage;
}
